import { useState } from 'react';

import { Link } from 'react-router-dom';

import {
  Avatar,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  keyframes,
  styled,
} from '@mui/material';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';

import useSignOut from '@/features/authentication/hooks/useSignOut';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { getLeagueLogo } from '@/shared/services/getLeagueLogo';
import { getFirstLettersOfName, getRandomPastelColor } from '@/shared/services';


// Staggered slide-in animation (left to right)
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const STAGGER_DELAY = 25; // ms between each item

// Animated components
const AnimatedListItem = styled(ListItem)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.12s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

const AnimatedListItemButton = styled(ListItemButton)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.12s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

const AnimatedDivider = styled(Divider)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.1s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

export const PageAndAccountMenu = ({ handleClose }: { handleClose: () => void }) => {
  const {
    currentOrg,
    currentLeague,
    availableLeagues,
    availableOrgs,
    actions: { updateDefaultLeague, updateDefaultOrg },
    hasRole,
  } = useUserStateContext();

  const [subMenuOpen, setSubMenuIsOpen] = useState(false);
  const [subMenuLeagueOpen, setSubMenuLeagueIsOpen] = useState(true);

  const handleSignOut = useSignOut();

  // Track animation index
  let itemIndex = 0;
  const getDelay = () => (itemIndex++) * STAGGER_DELAY;

  return (
    <List sx={{ paddingTop: 0, marginBottom: 4 }}>
      {currentOrg && [
        <AnimatedListItem key="org-info" delay={getDelay()}>
          <ListItemIcon sx={{ marginRight: 2 }}>
            <Avatar
              src={currentOrg.logoPath ?? undefined}
              sx={{ bgcolor: currentOrg.logoPath ? 'transparent' : getRandomPastelColor(currentOrg.name[0], currentOrg.name[2]) }}
              variant="rounded"
            >
              {getFirstLettersOfName(currentOrg.name)}
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={currentOrg.name} secondary="Organization" />
        </AnimatedListItem>,
        <AnimatedDivider key="org-divider" delay={getDelay()} />,
      ]}
      {Array.isArray(availableOrgs) &&
        availableOrgs.length > 1 && [
          <AnimatedListItem key="switch-org" disablePadding delay={getDelay()}>
            <ListItemButton
              onClick={() => {
                setSubMenuIsOpen(!subMenuOpen);
              }}
            >
              <ListItemText primary="Switch Organization" />
              {subMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </AnimatedListItem>,
          <Collapse key="org-collapse" in={subMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {availableOrgs.map((org) => (
                <ListItemButton
                  key={org.id}
                  sx={{ pl: 4 }}
                  onClick={() => {
                    updateDefaultOrg(org);
                    handleClose();
                  }}
                >
                  <ListItemText primary={org.name} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>,
          <AnimatedDivider key="switch-org-divider" delay={getDelay()} />,
        ]}

      {availableLeagues.length > 1 && [
          <AnimatedListItem key="switch-league" disablePadding delay={getDelay()}>
            <ListItemButton
              onClick={() => {
                setSubMenuLeagueIsOpen(!subMenuLeagueOpen);
              }}
            >
              <ListItemText primary="Switch League" />
            </ListItemButton>
          </AnimatedListItem>,
          <List key="league-list" component="div" disablePadding>
            {availableLeagues.map((league) => (
              <AnimatedListItemButton
                key={league.id}
                delay={getDelay()}
                sx={{
                  pl: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${getLeagueLogo(league)})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    opacity: league.id === currentLeague?.id ? 0.2 : 0,
                    transform: league.id === currentLeague?.id ? 'scale(1)' : 'scale(1.2)',
                    transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
                    zIndex: 1,
                    filter: 'blur(7px)',
                  },
                  '&:hover::after': {
                    opacity: 1,
                    transform: 'scale(0.9)',
                  },
                  '& .MuiListItemText-root': {
                    position: 'relative',
                    zIndex: 2,
                  },
                }}
                onClick={() => {
                  updateDefaultLeague(league);
                  handleClose();
                }}
              >
                <ListItemText
                  primary={league.displayName}
                  sx={{ textShadow: ' -1px 1px 10px rgba(0, 0, 0, 0.75)' }}
                />
              </AnimatedListItemButton>
            ))}
          </List>,
          <AnimatedDivider key="league-divider" delay={getDelay()} />,
        ]}

      <AnimatedListItem disablePadding delay={getDelay()}>
        <ListItemButton component={Link} to="/account-settings">
          <ListItemText primary="Account Settings & Preferences" />
        </ListItemButton>
      </AnimatedListItem>
      <AnimatedDivider delay={getDelay()} />
      <AnimatedListItem disablePadding delay={getDelay()}>
        <ListItemButton onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </AnimatedListItem>
    </List>
  );
};
